import React, { useMemo, useState } from "react";
import { Modal } from "antd";
import { ICONS } from "../../../../assets/icons";

const FLOW_DEFAULT_TITLE = "View Flow";
const FLOW_DEFAULT_SUBTITLE = "View Response";

// Flow payloads append timestamps to option labels; drop them so UI shows clean text.
const stripTrailingTimestamp = (value = "") =>
  String(value).replace(/\s+\d{10,}$/, "").trim();

const toTitleCase = (value = "") => {
  const cleaned = value.replace(/[_\s]+/g, " ").trim();
  if (!cleaned) return "";
  return cleaned
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
 

const formatFlowAnswer = (value) => {
  if (value === null || value === undefined) return null;
  const stringified = typeof value === "string" ? value : String(value);
  // Remove numeric prefixes such as "0_answer" and trailing timestamps like "Answer 1234567890123"
  const withoutPrefix = stringified.replace(/^\d+_/, "");
  const withoutTimestamp = stripTrailingTimestamp(withoutPrefix);
  return toTitleCase(withoutTimestamp);
};

const parseFlowResponses = (responseJson) => {
  if (!responseJson) return [];

  let parsed;
  try {
    parsed = typeof responseJson === "string" ? JSON.parse(responseJson) : responseJson;
  } catch (error) {
    console.warn("Unable to parse flow response JSON", error);
    return [];
  }

  const entries = Object.entries(parsed)
    .filter(([key]) => key !== "flow_token")
    .map(([key, value]) => {
      let screenIndex = Number.MAX_SAFE_INTEGER;
      let labelSection = key;

      // Case 1: "screen_2_question_name"
      const screenMatch = key.match(/^screen_(\d+)_/);
      if (screenMatch) {
        screenIndex = Number(screenMatch[1]);
        labelSection = key.slice(screenMatch[0].length);
      } else {
        // Case 2: "0_question_name" → treat leading number as screen index
        const numericPrefixMatch = key.match(/^(\d+)_/);
        if (numericPrefixMatch) {
          screenIndex = Number(numericPrefixMatch[1]);
          labelSection = key.slice(numericPrefixMatch[0].length);
        }
      }
      // --- NEW LOGIC END ---

      const labelParts = labelSection.split("_");
      const maybeIndex = labelParts[labelParts.length - 1];
      const questionLabelParts = /^\d+$/.test(maybeIndex)
        ? labelParts.slice(0, -1)
        : labelParts;
      const rawLabel = questionLabelParts.join(" ");
      const questionLabel = toTitleCase(stripTrailingTimestamp(rawLabel));

      const answersArray = Array.isArray(value) ? value : [value];
      const answers = answersArray
        .map((answer) => formatFlowAnswer(answer))
        .filter(Boolean);

      return {
        screenIndex,
        questionLabel,
        answers,
      };
    })
    .filter((entry) => entry.answers.length);

  const orderedEntries = entries.sort((a, b) => a.screenIndex - b.screenIndex);

  return orderedEntries.map((entry, index) => ({
    ...entry,
    questionNumber: index + 1,
    totalQuestions: orderedEntries.length,
  }));
};

const FlowResponseModal = ({ open, onClose, responses }) => (
  <Modal
    open={open}
    footer={null}
    centered
    title="Flow response"
    onCancel={onClose}
    destroyOnClose
    className="flow-response-modal"
  >
    <div className="max-h-[60vh] overflow-y-auto">
      {responses.length ? (
        responses.map((item) => (
          <div key={`${item.screenIndex}-${item.questionLabel}`} className="mt-3 pb-3 border-b">
            {/* <div className="text-sm font-semibold text-gray-700"> */}
              {/* {`Screen ${item.questionNumber} of ${item.totalQuestions}`} */}
            {/* </div> */}
            {item.questionLabel ? (
              <div className="text-xs text-gray-500 my-2">{item.questionLabel}</div>
            ) : null}
            <div className="mt-3 space-y-2">
              {item.answers.map((answer, idx) => (
                <div key={idx} className="text-sm text-gray-900">
                  {answer}
                </div>
              ))}
            </div>
          </div>
        ))
      ) : (
        <div className="text-sm text-gray-600">No flow responses available.</div>
      )}
    </div>
  </Modal>
);

const FlowReplyPreview = ({ flowReply, timestamp }) => {
  const [showModal, setShowModal] = useState(false);

  if (!flowReply) return null;

  const responses = useMemo(
    () => parseFlowResponses(flowReply.response_json),
    [flowReply.response_json]
  );

  const flowTitle = flowReply.name ? toTitleCase(flowReply.name) : FLOW_DEFAULT_TITLE;
  const flowSubtitle =
    flowReply.body && flowReply.body.toLowerCase() === "sent"
      ? FLOW_DEFAULT_SUBTITLE
      : flowReply.body || FLOW_DEFAULT_SUBTITLE;

  const handleCardClick = () => {
    setShowModal(true);
  };

  return (
    <>
      <div
        className={`w-full bg-[#f6f6f6] rounded-[0.6vw] rounded-tl-none p-[0.8vw] ${
          responses.length ? "cursor-pointer" : ""
        }`}
        onClick={handleCardClick}
      >
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
            <img
              src={ICONS.documentInbox}
              alt="Flow reply"
              className="w-4 h-4 object-contain"
            />
          </div>
          <div className="flex-1">
            <div className="text-[0.85rem] weight-semibold text-[#0d3a2b]">
              {flowTitle || FLOW_DEFAULT_TITLE}
            </div>
            <div className="text-[0.7rem] text-[#4b5563] flex items-center flex-wrap gap-2">
              <span>{flowSubtitle}</span>
              {timestamp ? (
                <span className="text-[0.65rem] text-[#6b7280]">{timestamp}</span>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <FlowResponseModal
        open={showModal}
        onClose={() => setShowModal(false)}
        responses={responses}
      />
    </>
  );
};

export default FlowReplyPreview;
